import React from 'react';
import { PresentableOrderStatusType } from '@1po/1po-bff-fe-spec/generated/order/response/model/OrderItem';
import { UserNotificationResponse } from '@1po/1po-bff-fe-spec/generated/user/response/UserNotificationResponse';
import { TFunction } from 'i18next';
import moment from 'moment';
import { DurationInputArg1 } from 'moment/moment';
import {
  ROUTER_CLIENT_ORDER_DETAIL,
  ROUTER_ESTIMATE,
  ROUTER_IN_PROGRESS_ORDERS,
  ROUTER_MY_NETWORK_ORDERS_TAB,
} from 'app/AppRouter';
import { ExclamationCircleIcon, InfoCircleIcon } from 'assets/icons';
import { getColor, getStatusIcon, getStatusLabel } from 'domains/order/OrderStatus';
import { PromotionTabParam } from 'domains/promotion/Promotion.types';
import { UserRole } from 'domains/user';
import { theme } from 'styles';
import { Icon, Link, Text } from 'UI';

interface NotificationLine {
  title: string;
  description: React.ReactNode;
  link: string;
  icon: React.ReactNode;
  disableLink?: boolean;
}

const SPLIT_HOURS_DAYS_VALUE = 50;

export const mapNotificationToType = (
  t: TFunction,
  notification: UserNotificationResponse,
  userRoles: UserRole[],
  onLinkClick?: () => void,
): NotificationLine | undefined => {
  const contextMap = notification.notificationContext?.contextMap;
  const getOrderLink = (internalOrderId: unknown) => {
    if (typeof internalOrderId === 'string') {
      return `${ROUTER_IN_PROGRESS_ORDERS}/${internalOrderId}`;
    }
    return ROUTER_IN_PROGRESS_ORDERS;
  };
  const getEstimateLink = (estimateUUID?: unknown) => {
    if (estimateUUID && typeof estimateUUID === 'string') {
      const urlParams = new URLSearchParams();
      urlParams.set(PromotionTabParam, 'estimate');
      urlParams.set('estimateId', estimateUUID);
      return `${ROUTER_ESTIMATE}?${urlParams}`;
    }
    return ROUTER_ESTIMATE;
  };
  const getManageOrderLink = (sellerOrderNumber: unknown) => {
    if (typeof sellerOrderNumber === 'string' && sellerOrderNumber.length > 0) {
      return `${ROUTER_CLIENT_ORDER_DETAIL}/${sellerOrderNumber}`;
    }
    return ROUTER_MY_NETWORK_ORDERS_TAB;
  };
  switch (notification.notificationType) {
    case 'ORDER_STATUS_CHANGE': {
      const orderId = contextMap?.['orderId'];
      const orderStatus = contextMap?.['orderStatus'];
      const internalOrderId = contextMap?.['callerId'];
      const color = getColor(orderStatus as PresentableOrderStatusType);
      return {
        title: t('notifications.order_updated.title', 'Your order {{orderId}} status updated.', { orderId }),
        description: (
          <Text type="base">
            {t(
              'notifications.order_updated.description',
              'Your order {{orderId}} has been updated to: {{orderStatus}}',
              { orderId, orderStatus: getStatusLabel(orderStatus as PresentableOrderStatusType) },
            )}
          </Text>
        ),
        link: getOrderLink(internalOrderId),
        disableLink: !userRoles.includes(UserRole.COMMAND) && !userRoles.includes(UserRole.CONNECT_COMMANDE),
        icon: (
          <Icon
            IconComponent={getStatusIcon(orderStatus as PresentableOrderStatusType)}
            size={25}
            color={theme.color[color]}
          />
        ),
      };
    }
    case 'REFERENCE_STATUS_CHANGE': {
      const orderId = contextMap?.['orderId'];
      const referenceStatus = contextMap?.['referenceStatus'];
      const referenceId = contextMap?.['referenceId'];
      const referenceName = contextMap?.['referenceName'];
      const internalOrderId = contextMap?.['callerId'];
      const color = getColor(referenceStatus as PresentableOrderStatusType);

      return {
        title: t('notifications.reference_updated.title', 'Your reference {{referenceId}} status updated.', {
          referenceId,
        }),
        description: (
          <Text type="base">
            {t(
              'notifications.reference_updated.description',
              'Your reference {{referenceId}}, {{referenceName}} from the Order {{orderId}} has been updated to: {{referenceStatus}}',
              {
                referenceId,
                referenceName,
                orderId,
                referenceStatus: getStatusLabel(referenceStatus as PresentableOrderStatusType),
              },
            )}
          </Text>
        ),
        link: getOrderLink(internalOrderId),
        disableLink: !userRoles.includes(UserRole.COMMAND) && !userRoles.includes(UserRole.CONNECT_COMMANDE),
        icon: (
          <Icon
            IconComponent={getStatusIcon(referenceStatus as PresentableOrderStatusType)}
            size={25}
            color={theme.color[color]}
          />
        ),
      };
    }
    case 'ESTIMATE_REACHING_END_OF_LIFE': {
      const estimateUUID = contextMap?.['estimateUUID'];
      const isUrgentRaw = contextMap?.['isUrgent'];
      const isUrgent = isUrgentRaw === 'true';
      const remainingDurationRaw = contextMap?.['remainingDuration'];
      const remainingDuration = moment.duration(remainingDurationRaw as DurationInputArg1);
      const remainingDays = remainingDuration.asDays();
      const remainingHours = remainingDuration.asHours();
      const sequenceNumber = contextMap?.['sequenceNumber'];

      const getTitle = () => {
        if (remainingHours > SPLIT_HOURS_DAYS_VALUE) {
          return isUrgent
            ? t(
                'notifications.estimate_end_of_life.remaining_days_urgent.title',
                '{{remainingDays}} days before expiration date : URGENT :',
                { remainingDays: remainingDays.toFixed() },
              )
            : t(
                'notifications.estimate_end_of_life.remaining_days.title',
                '{{remainingDays}} days before expiration date :',
                { remainingDays: remainingDays.toFixed() },
              );
        }
        return t(
          'notifications.estimate_end_of_life.remaining_hours.title',
          '{{remainingHours}} hours before expiration date : URGENT :',
          {
            remainingHours: remainingHours.toFixed(),
          },
        );
      };

      const getDescription = (): React.ReactNode => {
        if (remainingHours > SPLIT_HOURS_DAYS_VALUE) {
          return (
            <Text type="base">
              {t(
                'notifications.estimate_end_of_life.remaining_days.description',
                `Your Estimate {{sequenceNumber}} is approaching its end of Life ({{remainingDays}} days remaining)`,
                { sequenceNumber, remainingDays: remainingDays.toFixed() },
              )}
            </Text>
          );
        }
        return (
          <Text type="base">
            {t(
              'notifications.estimate_end_of_life.remaining_hours.description',
              `Your Estimate {{sequenceNumber}} is approaching its end of Life ({{remainingHours}} hours remaining)`,
              { sequenceNumber, remainingHours: remainingHours.toFixed() },
            )}
          </Text>
        );
      };

      return {
        title: getTitle(),
        description: getDescription(),
        link: getEstimateLink(estimateUUID),
        icon: (
          <Icon
            IconComponent={isUrgent ? ExclamationCircleIcon : InfoCircleIcon}
            size={25}
            color={isUrgent ? theme.color.warning : theme.color.info}
          />
        ),
      };
    }
    case 'ORDER_NEEDS_VALIDATION': {
      // The notification context carries `sellerOrderNumber` (RP4 seller order number, e.g. 00010699)
      // pre-resolved by the backend saga. The order detail page is keyed by this value.
      // `callerId` (= buyerCallerNumber, e.g. 2604308000000005) is kept for backward compatibility.
      const sellerOrderNumber = contextMap?.['sellerOrderNumber'];
      return {
        title: t('notifications.order_needs_validation.title', 'One or more orders need your attention.'),
        description: (
          <>
            <Text type="base">
              {t(
                'notifications.order_needs_validation.description',
                "You've just received new Orders, some might need validation or review, please go to",
              )}
            </Text>{' '}
            <Link to={ROUTER_MY_NETWORK_ORDERS_TAB} additionalAction={onLinkClick}>
              <Text type="base" color="clear_blue" hoverUnderLine data-cy={'manage-my_orders-link'}>
                {t('notifications.validation.my_orders_link', 'Manage my Orders')}
              </Text>
            </Link>
          </>
        ),
        link: getManageOrderLink(sellerOrderNumber),
        disableLink: !userRoles.includes(UserRole.COMMAND) && !userRoles.includes(UserRole.CONNECT_COMMANDE),
        icon: <Icon IconComponent={ExclamationCircleIcon} size={25} color={theme.color.warning} />,
      };
    }
    default:
      return undefined;
  }
};
